import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { messagingPlatformAPI, notificationsAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadMergedNotifications, markNotificationReadMerged } from '../utils/mergePlatformNotifications';
import { usePermissions } from '../hooks/usePermissions';
import ApprovalRequests from './ApprovalRequests';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';

const Notifications = () => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const { success, error } = useToast();
  const { hasPermission } = usePermissions();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canApprovalsTab = hasPermission('contributions.approve');
  const hubTab =
    searchParams.get('tab') === 'approvals' && canApprovalsTab ? 'approvals' : 'inbox';
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  // Charger les notifications
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const merged = await loadMergedNotifications(notificationsAPI, messagingPlatformAPI);
      setNotifications(merged);
    } catch (err) {
      console.error('Erreur lors du chargement des notifications:', err);
      error(t('notificationsPage.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'approvals' && !canApprovalsTab) {
      navigate('/notifications', { replace: true });
    }
  }, [searchParams, canApprovalsTab, navigate]);

  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    try {
      const n = notifications.find((x) => x.id === notificationId);
      await markNotificationReadMerged(n, notificationsAPI, messagingPlatformAPI);
      setNotifications((prev) => prev.map((x) => (x.id === notificationId ? { ...x, read: true } : x)));
    } catch (err) {
      console.error('Erreur lors du marquage de la notification:', err);
      error(t('notificationsPage.markError'));
    }
  };

  // Marquer toutes comme lues
  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length === 0) return;
    
    try {
      await Promise.all(
        unreadNotifications.map((n) => markNotificationReadMerged(n, notificationsAPI, messagingPlatformAPI))
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      success(t('notificationsPage.markAllSuccess'));
    } catch (err) {
      console.error('Erreur lors du marquage de toutes les notifications:', err);
      error(t('notificationsPage.markAllError'));
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return (
          <div className="p-1.5 md:p-2 bg-green-100 rounded-lg">
            <svg className="w-4 h-4 md:w-5 md:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="p-1.5 md:p-2 bg-yellow-100 rounded-lg">
            <svg className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="p-1.5 md:p-2 bg-red-100 rounded-lg">
            <svg className="w-4 h-4 md:w-5 md:h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="p-1.5 md:p-2 bg-[#edf4ff] rounded-lg">
            <svg className="w-4 h-4 md:w-5 md:h-5 text-[#113a5b]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
        );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(localeFmt, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const readCount = notifications.length - unreadCount;

  return (
    <div className="min-h-[60vh] space-y-2.5 sm:space-y-4 md:space-y-6">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate('/notifications', { replace: true })}
          className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition min-h-[40px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
            hubTab === 'inbox'
              ? 'mm-chip mm-chip--on shadow-sm'
              : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
          }`}
        >
          {t('notificationsPage.tabInbox')}
        </button>
        {canApprovalsTab && (
          <button
            type="button"
            onClick={() => navigate('/notifications?tab=approvals', { replace: true })}
            className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition min-h-[40px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
              hubTab === 'approvals'
                ? 'mm-chip mm-chip--on shadow-sm'
                : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
            }`}
          >
            {t('notificationsPage.tabApprovals')}
          </button>
        )}
      </div>

      {hubTab === 'approvals' ? (
        <ApprovalRequests embedded />
      ) : (
        <>
          <MemberMobileShell>
            <MemberMobileHero
              label={t('notificationsPage.kicker')}
              title={
                unreadCount > 0
                  ? t('notificationsPage.unreadCount', { count: unreadCount })
                  : t('notificationsPage.descUpToDate')
              }
              hint={t('notificationsPage.chipAll', { count: notifications.length })}
              tone={unreadCount > 0 ? 'warn' : 'ok'}
              actions={
                unreadCount > 0 ? (
                  <button
                    type="button"
                    onClick={markAllAsRead}
                    className="mm-cta flex-1"
                  >
                    {t('notificationsPage.markAllRead')}
                  </button>
                ) : null
              }
            />
          </MemberMobileShell>

          {/* Header */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{t('notificationsPage.headerTitle')}</h1>
                <p className="text-gray-600 text-xs sm:text-sm">
                  {unreadCount > 0
                    ? t('notificationsPage.subtitleUnread', { count: unreadCount })
                    : t('notificationsPage.subtitleAll')}
                  {canApprovalsTab && (
                    <span className="block mt-1 text-gray-500">{t('notificationsPage.approvalsHint')}</span>
                  )}
                </p>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition font-medium text-sm min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                >
                  {t('notificationsPage.markAllRead')}
                </button>
              )}
            </div>
          </div>

          {/* Filtres */}
          <div className="mm-card p-2.5 md:rounded-lg md:border md:border-gray-200 md:bg-white md:p-4 md:shadow-sm md:[backdrop-filter:none]">
            <div className="flex gap-1.5 md:gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <button
                onClick={() => setFilter('all')}
                className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full md:rounded-lg font-medium text-xs md:text-sm whitespace-nowrap transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                  filter === 'all' ? 'mm-chip mm-chip--on' : 'mm-chip mm-chip--off'
                }`}
              >
                {t('notificationsPage.filterAll', { count: notifications.length })}
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full md:rounded-lg font-medium text-xs md:text-sm whitespace-nowrap transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                  filter === 'unread' ? 'mm-chip mm-chip--on' : 'mm-chip mm-chip--off'
                }`}
              >
                {t('notificationsPage.filterUnread', { count: unreadCount })}
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full md:rounded-lg font-medium text-xs md:text-sm whitespace-nowrap transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                  filter === 'read' ? 'mm-chip mm-chip--on' : 'mm-chip mm-chip--off'
                }`}
              >
                {t('notificationsPage.filterRead', { count: notifications.length - unreadCount })}
              </button>
            </div>
          </div>

          {/* Liste des notifications */}
          <div className="mm-card overflow-hidden md:rounded-lg md:border md:border-gray-200 md:bg-white md:shadow-sm md:[backdrop-filter:none]">
            {loading ? (
              <div className="p-2.5 md:p-6 space-y-2.5 md:space-y-3 animate-pulse">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-2xl border border-slate-200/80 bg-white/60 p-2.5 md:p-3.5">
                    <div className="flex items-start gap-2.5 md:gap-3">
                      <div className="h-7 w-7 md:h-9 md:w-9 rounded-lg bg-slate-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3.5 w-1/2 rounded bg-slate-200" />
                        <div className="h-3 w-full rounded bg-slate-100" />
                        <div className="h-3 w-2/3 rounded bg-slate-100" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="text-center py-12 px-4">
                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <p className="mt-4 text-gray-900 font-medium">
                  {filter === 'unread'
                    ? t('notificationsPage.emptyUnread')
                    : filter === 'read'
                      ? t('notificationsPage.emptyRead')
                      : t('notificationsPage.emptyAll')}
                </p>
                <p className="mt-2 text-sm text-gray-500">{t('notificationsPage.emptyHint')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-2.5 md:p-6 hover:bg-slate-50 transition-colors ${
                      !notification.read ? 'bg-[#edf4ff]/60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2.5 md:gap-4">
                      <div className="flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className={`text-base sm:text-lg font-semibold ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                {notification.title}
                              </h3>
                              {!notification.read && (
                                <span className="flex-shrink-0 w-2 h-2 bg-[#2f6fd4] rounded-full"></span>
                              )}
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              {formatDate(notification.created_at)}
                            </p>
                          </div>
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="flex-shrink-0 px-3 py-2 text-xs font-semibold text-[#113a5b] hover:bg-[#edf4ff] rounded-xl transition min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                            >
                              {t('notificationsPage.markRead')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {unreadCount > 0 && (
            <div className="md:hidden fixed right-4 z-40 bottom-[calc(env(safe-area-inset-bottom)+8.75rem)]">
              <button
                type="button"
                onClick={markAllAsRead}
                aria-label={t('notificationsPage.fabAria')}
                className="mm-cta h-12 rounded-2xl px-4 text-sm shadow-xl"
              >
                {t('notificationsPage.fabShort')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Notifications;


